import { useMutation } from '@tanstack/react-query';
import ChatService from '../services/chat.service';

const chatService = new ChatService();

/**
 * Validates, sanitizes, and encodes the redirect URL.
 *
 * @param url - The URL to validate, sanitize, and encode.
 * @returns A sanitized and encoded URL if valid, otherwise throws an error.
 */
const sanitizeAndEncodeRedirectUrl = (url: string): string => {
    try {
        const sanitizedUrl = new URL(url); // Validates the URL format
        if (!sanitizedUrl.protocol.startsWith('http')) {
            throw new Error('Invalid URL protocol. Only HTTP and HTTPS are allowed.');
        }

        // Encode the URL to avoid conflicts with special characters
        sanitizedUrl.search = encodeURIComponent(sanitizedUrl.search);
        return sanitizedUrl.toString();
    } catch (error) {
        throw new Error('Invalid redirect URL provided.');
    }
};

/**
 * Custom hook to handle Google authentication redirection.
 *
 * @returns An object containing the mutation state and the `mutate` function to trigger the redirection.
 */
export const useGetGoogleAuthCode = () => {
    return useMutation({
        mutationFn: async (redirectUrl: string) => {
            if (!redirectUrl) {
                throw new Error('Redirect URL is required');
            }

            // Sanitize, validate, and encode the redirect URL
            const sanitizedUrl = sanitizeAndEncodeRedirectUrl(redirectUrl);

            // Call the ChatService method with the sanitized and encoded URL
            return await chatService.redirectToGoogleAuth(sanitizedUrl);
        },
    });
};
